import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import useEmblaCarousel from 'embla-carousel-react'
import AutoScroll from 'embla-carousel-auto-scroll'
import companies from '../data/companies.json'

const LogoMarquee = () => {
  const autoScrollRef = React.useRef(null)
  const [validLogos, setValidLogos] = useState(companies)

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, skipSnaps: false },
    [AutoScroll({ playOnInit: true, speed: 0.9 })]
  )

  const getLogoSrc = domain => {
    const name = domain.split('.')[0]
    const extensions = ['png', 'svg', 'gif', 'webp', 'jpg']
    return extensions.map(ext => `/logos/${ name }.${ ext }`)
  }

  const handleLogoError = (e, company, extensions, extensionIndex) => {
    if (extensionIndex < extensions.length - 1) {
      e.target.src = extensions[extensionIndex + 1]
      e.target.onerror = err => handleLogoError(err, company, extensions, extensionIndex + 1)
    } else {
      setValidLogos(prev => prev.filter(c => c.domain !== company.domain))
    }
  }

  useEffect(() => {
    if (!emblaApi || validLogos.length < 5) return

    const autoScroll = emblaApi.plugins().autoScroll
    if (!autoScroll) return

    autoScrollRef.current = autoScroll

    const handleVisibilityChange = () => {
      if (document.hidden) {
        autoScroll.stop()
      } else {
        autoScroll.play()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [emblaApi, validLogos.length])

  if (validLogos.length === 0) {
    return null
  }

  return (
    <Container>
      <EmblaViewport ref={emblaRef}>
        <EmblaContainer>
          {validLogos.map(company => {
            const extensions = getLogoSrc(company.domain)
            return (
              <EmblaSlide key={company.domain}>
                <LogoWrapper>
                  <LogoImg
                    src={extensions[0]}
                    alt={company.name}
                    title={company.name}
                    onError={e => handleLogoError(e, company, extensions, 0)}
                  />
                </LogoWrapper>
              </EmblaSlide>
            )
          })}
        </EmblaContainer>
      </EmblaViewport>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  margin: 0;
  padding: 0.5rem 0;
  position: relative;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 120px,
    black calc(100% - 120px),
    transparent 100%
  );
`

const EmblaViewport = styled.div`
  overflow: hidden;
`

const EmblaContainer = styled.div`
  display: flex;
  gap: 3rem;
  padding: 0 2rem;
`

const EmblaSlide = styled.div`
  flex: 0 0 auto;
  min-width: 0;
`

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  min-width: auto;
`

const LogoImg = styled.img`
  height: 100%;
  width: auto;
  filter: grayscale(1) opacity(0.6);
  transition: filter 0.3s ease;
  object-fit: contain;

  /* Default max width for other logos */
  &:not([src*="adobe"]):not([src*="vizcom"]):not([src*="mozilla"]):not([src*="envoy"]):not([src*="shipt"]):not([src*="wander"]):not([src*="imgur"]):not([src*="autodesk"]):not([src*="hims"]):not([src*="carbonhealth"]) {
    max-width: 130px;
  }

  /* Large logos - reduce size */
  &[src*="adobe"] {
    max-width: 130px;
    object-fit: contain;
    transform: scale(0.6);
  }

  &[src*="mozilla"],
  &[src*="envoy"],
  &[src*="shipt"],
  &[src*="wander"] {
    max-width: 90px;
  }

  &[src*="imgur"] {
    max-width: 70px;
  }

  /* Small logos - enlarge */
  &[src*="vizcom"] {
    width: 130px;
    max-width: none;
    object-fit: cover;
    object-position: center;
  }

  &[src*="autodesk"],
  &[src*="hims"],
  &[src*="carbonhealth"] {
    max-width: 180px;
  }

  &:hover {
    filter: grayscale(0) opacity(1);
  }
`

export default LogoMarquee
